'use client';

import { c as _c } from "react/compiler-runtime";
import type React from 'react';
import { useCallback, useEffect, useId, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { usePopper } from 'react-popper';
import type { ControlProps, GroupBase, MenuListProps, OptionProps } from 'react-select';
import ReactSelect from 'react-select';
import Hint from '../../hint';
import type { LabelTooltipProps } from '../../label';
import Label from '../../label';
import type { BaseSelectOption } from './base-select.types';
import modifiers from './base-select.utils';
import Control from './components/control';
import EmptyState from './components/empty-state';
import MenuList from './components/menu-list';
import Option from './components/option';
import Picker from './components/picker';
import type { ItemProps } from './components/picker/components/item';
export type BaseSelectSize = 'compact' | 'default';
const TOP_OFFSET = 40;
export type BaseSelectProps<Option extends BaseSelectOption> = {
  disabled?: boolean;
  emptyStateText?: string;
  hasError?: boolean;
  hint?: string;
  id?: string;
  label?: string;
  labelTooltip?: LabelTooltipProps;
  name?: string;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  onChange?: (newOption: Option) => void;
  options: Option[];
  renderTrigger?: (options: {
    isOpen: boolean;
    onClick: () => void;
    selectedOption?: Option;
    size?: BaseSelectSize;
    testID?: string;
    hasIcon?: boolean;
  }) => React.ReactNode;
  searchPlaceholder?: string;
  size?: BaseSelectSize;
  value?: Option;
  OptionComponent?: React.ComponentType<OptionProps<Option, false, GroupBase<Option>>>;
  testID?: string;
  MobileOptionComponent?: React.ComponentType<ItemProps>;
  popper?: boolean;
};
const BaseSelect = t0 => {
  const $ = _c(76);
  const {
    disabled,
    emptyStateText,
    hasError,
    hint,
    id: baseId,
    label,
    labelTooltip,
    name,
    onBlur,
    onChange,
    OptionComponent: t1,
    options: t2,
    renderTrigger,
    searchPlaceholder,
    size: t3,
    testID,
    value,
    MobileOptionComponent,
    popper: t4
  } = t0;
  const OptionComponent = t1 === undefined ? Option : t1;
  let t5;
  if ($[0] !== t2) {
    t5 = t2 === undefined ? [] : t2;
    $[0] = t2;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const options = t5;
  const size = t3 === undefined ? "default" : t3;
  const popper = t4 === undefined ? false : t4;
  const {
    t
  } = useTranslation("ui");
  const internalId = useId();
  const id = baseId || internalId;
  const [isOpen, setOpen] = useState(false);
  const [referenceElement, setReferenceElement] = useState();
  const [popperElement, setPopperElement] = useState();
  const t6 = popper ? "absolute" : "fixed";
  let t7;
  if ($[2] !== t6) {
    t7 = {
      strategy: t6,
      modifiers
    };
    $[2] = t6;
    $[3] = t7;
  } else {
    t7 = $[3];
  }
  const {
    styles,
    attributes
  } = usePopper(referenceElement, popperElement, t7);
  const isSearchable = options.length > 10;
  const [updatedValue, setUpdatedValue] = useState(value);
  const [mobileSheetHeight, setMobileSheetHeight] = useState(0);
  let t8;
  let t9;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      const body = document.querySelector("body");
      const setBodyHeight = () => {
        setMobileSheetHeight(window.innerHeight - TOP_OFFSET || 0);
      };
      const resizeObserver = new ResizeObserver(setBodyHeight);
      const startResizeObserve = () => {
        if (body) {
          resizeObserver.observe(body);
        }
      };
      const stopResizeObserve = () => {
        if (body) {
          resizeObserver.unobserve(body);
        }
      };
      startResizeObserve();
      return stopResizeObserve;
    };
    t9 = [];
    $[4] = t8;
    $[5] = t9;
  } else {
    t8 = $[4];
    t9 = $[5];
  }
  useEffect(t8, t9);
  let t10;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => {
      setOpen(false);
    };
    $[6] = t10;
  } else {
    t10 = $[6];
  }
  const closeDropdown = t10;
  let t11;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = () => {
      setOpen(_temp);
    };
    $[7] = t11;
  } else {
    t11 = $[7];
  }
  const toggleDropdown = t11;
  let t12;
  let t13;
  if ($[8] !== value) {
    t12 = () => {
      setUpdatedValue(value);
    };
    t13 = [value];
    $[8] = value;
    $[9] = t12;
    $[10] = t13;
  } else {
    t12 = $[9];
    t13 = $[10];
  }
  useEffect(t12, t13);
  let t14;
  if ($[11] !== onChange) {
    t14 = newOption => {
      setUpdatedValue(newOption);
      onChange?.(newOption);
      closeDropdown();
    };
    $[11] = onChange;
    $[12] = t14;
  } else {
    t14 = $[12];
  }
  const handleChange = t14;
  let t15;
  if ($[13] !== emptyStateText || $[14] !== t) {
    t15 = emptyStateText ?? t(_temp2);
    $[13] = emptyStateText;
    $[14] = t;
    $[15] = t15;
  } else {
    t15 = $[15];
  }
  const localizedEmptyStateText = t15;
  let t16;
  if ($[16] !== localizedEmptyStateText) {
    t16 = () => <EmptyState>{localizedEmptyStateText}</EmptyState>;
    $[16] = localizedEmptyStateText;
    $[17] = t16;
  } else {
    t16 = $[17];
  }
  const renderEmptyState = t16;
  let t17;
  if ($[18] !== id || $[19] !== label || $[20] !== labelTooltip) {
    t17 = label && <Label tooltip={labelTooltip} htmlFor={id}>{label}</Label>;
    $[18] = id;
    $[19] = label;
    $[20] = labelTooltip;
    $[21] = t17;
  } else {
    t17 = $[21];
  }
  const t18 = updatedValue?.value;
  let t19;
  if ($[22] !== disabled || $[23] !== onChange || $[24] !== options) {
    t19 = ev => {
      const newVal = ev.target.value;
      const option = options.find(opt => opt.value === newVal);
      if (option && !disabled) {
        setUpdatedValue(option);
        onChange?.(option);
      }
    };
    $[22] = disabled;
    $[23] = onChange;
    $[24] = options;
    $[25] = t19;
  } else {
    t19 = $[25];
  }
  let t20;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = {
      height: 0,
      width: 0,
      opacity: 0,
      position: "absolute"
    };
    $[26] = t20;
  } else {
    t20 = $[26];
  }
  let t21;
  if ($[27] !== t18 || $[28] !== t19) {
    t21 = <input value={t18} onChange={t19} style={t20} />;
    $[27] = t18;
    $[28] = t19;
    $[29] = t21;
  } else {
    t21 = $[29];
  }
  let t22;
  if ($[30] !== internalId || $[31] !== isOpen || $[32] !== renderTrigger || $[33] !== size || $[34] !== updatedValue) {
    t22 = renderTrigger?.({
      isOpen,
      onClick: toggleDropdown,
      selectedOption: updatedValue,
      size,
      testID: internalId
    });
    $[30] = internalId;
    $[31] = isOpen;
    $[32] = renderTrigger;
    $[33] = size;
    $[34] = updatedValue;
    $[35] = t22;
  } else {
    t22 = $[35];
  }
  let t23;
  if ($[36] !== t22) {
    t23 = <div ref={setReferenceElement}>{t22}</div>;
    $[36] = t22;
    $[37] = t23;
  } else {
    t23 = $[37];
  }
  let t24;
  if ($[38] !== OptionComponent || $[39] !== attributes || $[40] !== disabled || $[41] !== handleChange || $[42] !== id || $[43] !== internalId || $[44] !== isOpen || $[45] !== isSearchable || $[46] !== name || $[47] !== onBlur || $[48] !== options || $[49] !== renderEmptyState || $[50] !== searchPlaceholder || $[51] !== styles || $[52] !== t || $[53] !== updatedValue) {
    t24 = isOpen && !isMobile && <div data-dd-privacy="mask" data-dd-action-name={`select-${internalId}`} data-testid={`select-${internalId}`} ref={setPopperElement} className="text-left" style={{
      ...styles.popper,
      zIndex: 10
    }} {...attributes.popper}><ReactSelect id={id} autoFocus={true} backspaceRemovesValue={false} controlShouldRenderValue={false} components={{
        Control: isSearchable ? Control as React.ComponentType<ControlProps<Option, false, GroupBase<Option>>> : _temp3,
        DropdownIndicator: null,
        IndicatorSeparator: null,
        MenuList: MenuList as React.ComponentType<MenuListProps<Option, false, GroupBase<Option>>>,
        Option: OptionComponent
      }} styles={{
        container: _temp4,
        placeholder: _temp5,
        control: _temp6,
        input: _temp7,
        menu: _temp8
      }} hideSelectedOptions={false} isDisabled={disabled} isSearchable={isSearchable} maxMenuHeight={180} menuIsOpen={true} name={name} noOptionsMessage={renderEmptyState} onBlur={onBlur} onChange={handleChange} onMenuClose={closeDropdown} options={options} placeholder={searchPlaceholder ?? t(_temp9)} value={updatedValue} /></div>;
    $[38] = OptionComponent;
    $[39] = attributes;
    $[40] = disabled;
    $[41] = handleChange;
    $[42] = id;
    $[43] = internalId;
    $[44] = isOpen;
    $[45] = isSearchable;
    $[46] = name;
    $[47] = onBlur;
    $[48] = options;
    $[49] = renderEmptyState;
    $[50] = searchPlaceholder;
    $[51] = styles;
    $[52] = t;
    $[53] = updatedValue;
    $[54] = t24;
  } else {
    t24 = $[54];
  }
  let t25;
  if ($[55] !== MobileOptionComponent || $[56] !== handleChange || $[57] !== id || $[58] !== isOpen || $[59] !== mobileSheetHeight || $[60] !== options || $[61] !== renderEmptyState || $[62] !== searchPlaceholder || $[63] !== updatedValue) {
    t25 = isMobile && <Picker id={id} value={updatedValue} open={isOpen} placeholder={searchPlaceholder} onClose={closeDropdown} renderEmptyState={renderEmptyState} options={options} onChange={option_0 => handleChange(option_0 as Option)} height={mobileSheetHeight} OptionComponent={MobileOptionComponent} />;
    $[55] = MobileOptionComponent;
    $[56] = handleChange;
    $[57] = id;
    $[58] = isOpen;
    $[59] = mobileSheetHeight;
    $[60] = options;
    $[61] = renderEmptyState;
    $[62] = searchPlaceholder;
    $[63] = updatedValue;
    $[64] = t25;
  } else {
    t25 = $[64];
  }
  let t26;
  if ($[65] !== hasError || $[66] !== hint) {
    t26 = hint && <Hint hasError={hasError}>{hint}</Hint>;
    $[65] = hasError;
    $[66] = hint;
    $[67] = t26;
  } else {
    t26 = $[67];
  }
  let t27;
  if ($[68] !== t17 || $[69] !== t21 || $[70] !== t23 || $[71] !== t24 || $[72] !== t25 || $[73] !== t26 || $[74] !== testID) {
    t27 = <div data-testid={testID} className="fp-dropdown">{t17}{t21}{t23}{t24}{t25}{t26}</div>;
    $[68] = t17;
    $[69] = t21;
    $[70] = t23;
    $[71] = t24;
    $[72] = t25;
    $[73] = t26;
    $[74] = testID;
    $[75] = t27;
  } else {
    t27 = $[75];
  }
  return t27;
};
export default BaseSelect;
function _temp(currentOpen) {
  return !currentOpen;
}
function _temp2($) {
  return $.components.internal["base-select"]["empty-state-text-default"];
}
function _temp3() {
  return null;
}
function _temp4() {
  return {
    background: "var(--dropdown-bg)",
    borderColor: "var(--dropdown-border-color)",
    borderRadius: "var(--dropdown-border-radius)",
    borderStyle: "solid",
    borderWidth: "var(--dropdown-border-width)",
    boxShadow: "var(--dropdown-elevation)",
    marginTop: "8px",
    overflow: "hidden",
    width: "100%"
  };
}
function _temp5(provided) {
  return {
    ...provided,
    fontFamily: "inherit",
    fontWeight: 400,
    fontSize: "0.875rem",
    lineHeight: "1.25rem"
  };
}
function _temp6() {
  return {
    alignItems: "center",
    background: "var(--input-bg)",
    backgroundColor: "var(--dropdown-bg)",
    borderRadiusTopLeftRadius: "var(--dropdown-border-radius)",
    borderRadiusTopRightRadius: "var(--dropdown-border-radius)",
    display: "flex",
    height: 40
  };
}
function _temp7(provided_0) {
  return {
    ...provided_0,
    fontFamily: "inherit",
    fontWeight: 400,
    fontSize: "0.875rem",
    lineHeight: "1.25rem"
  };
}
function _temp8() {
  return {};
}
function _temp9($_0) {
  return $_0.components.internal["base-select"]["search-placeholder-default"];
}